// Shared catalog for business pages: optional plugin modules + per-type demo content.
import {
  CalendarClock, Users, Gift, Images, Megaphone, Clock, MapPin,
  UtensilsCrossed, Armchair, Star, HelpCircle, Mail, Info, Contact,
} from 'lucide-react';

// ---- Plugin (papildmodulis) registry -------------------------------------
export const PLUGINS = {
  calendar:     { label: 'Kalendārs & rezervācijas', desc: 'Klienti izvēlas laiku un rezervē tiešsaistē', icon: CalendarClock },
  specialists:  { label: 'Speciālisti / Meistari', desc: 'Komandas saraksts ar foto; klients izvēlas, pie kā pierakstīties', icon: Users },
  gift_cards:   { label: 'Dāvanu kartes', desc: 'Pārdod dāvanu kartes ar dažādām summām', icon: Gift },
  gallery:      { label: 'Galerija', desc: 'Foto galerija ar darbiem vai telpām', icon: Images },
  promotions:   { label: 'Akcijas & piedāvājumi', desc: 'Izcel īpašos piedāvājumus un atlaides', icon: Megaphone },
  hours:        { label: 'Darba laiki', desc: 'Parādi nedēļas darba laikus', icon: Clock },
  map:          { label: 'Karte & atrašanās vieta', desc: 'Adrese ar karti un navigāciju', icon: MapPin },
  menu:         { label: 'Ēdienkarte', desc: 'Ēdienu kategorijas ar cenām un aprakstiem', icon: UtensilsCrossed },
  tables:       { label: 'Galdiņu rezervācija', desc: 'Klienti rezervē galdiņu noteiktā laikā', icon: Armchair },
  reviews:      { label: 'Atsauksmes & vērtējumi', desc: 'Klientu atsauksmes ar zvaigžņu vērtējumu', icon: Star },
  faq:          { label: 'BUJ (biežāk uzdotie jautājumi)', desc: 'Jautājumi un atbildes vienuviet', icon: HelpCircle },
  contact_form: { label: 'Kontaktforma', desc: 'Klienti nosūta ziņu tieši no lapas', icon: Mail },
  about:        { label: 'Par mums', desc: 'Garš apraksta teksts par uzņēmumu', icon: Info },
  contacts:     { label: 'Kontakti', desc: 'Tālrunis, e-pasts, mājaslapa, Facebook, Instagram', icon: Contact },
};

// Which plugins make sense for each business type.
const KIND_PLUGINS = {
  general:    ['calendar', 'specialists', 'gift_cards', 'gallery', 'promotions', 'hours', 'map', 'reviews', 'faq', 'contact_form'],
  special:    ['menu', 'tables', 'calendar', 'specialists', 'gift_cards', 'gallery', 'promotions', 'hours', 'map', 'reviews', 'faq', 'contact_form', 'about', 'contacts'],
  salon:      ['calendar', 'specialists', 'gift_cards', 'gallery', 'promotions', 'hours', 'map', 'reviews', 'faq', 'contact_form'],
  health:     ['calendar', 'specialists', 'hours', 'map', 'faq', 'contact_form', 'reviews'],
  fitness:    ['calendar', 'specialists', 'gallery', 'promotions', 'hours', 'map', 'reviews', 'faq'],
  auto:       ['calendar', 'promotions', 'hours', 'map', 'reviews', 'faq', 'contact_form'],
  education:  ['calendar', 'specialists', 'gallery', 'promotions', 'faq', 'contact_form', 'reviews'],
  stay:       ['calendar', 'gallery', 'promotions', 'hours', 'map', 'reviews', 'faq', 'contact_form'],
  trade:      ['calendar', 'gallery', 'promotions', 'contact_form', 'reviews', 'faq'],
  realestate: ['calendar', 'gallery', 'map', 'contact_form', 'faq', 'reviews'],
};
const MENU_PLUGINS = ['menu', 'tables', 'gift_cards', 'gallery', 'promotions', 'hours', 'map', 'reviews', 'faq', 'contact_form'];
const CARD_PLUGINS = ['hours', 'map', 'gift_cards', 'promotions', 'gallery', 'contact_form', 'faq', 'reviews'];

// Resolve the available plugin ids for a template + kind.
export function pluginsFor(template, kind = 'general') {
  if (template === 'menu') return MENU_PLUGINS;
  if (template === 'business_card') return CARD_PLUGINS;
  return KIND_PLUGINS[kind] || KIND_PLUGINS.general;
}

// Modules that start OFF for a given template (opt-in). Existing templates keep
// their previous behaviour; only these newly-added optional modules begin
// disabled until the owner turns them on.
export const DEFAULT_OFF = {
  menu: ['gift_cards', 'contact_form'],
  business_card: ['gift_cards'],
  // "Special" pages (template 'reservations', kind 'special') carry a menu block
  // too, but the restaurant-style Ēdienkarte + Galdiņu rezervācija modules start
  // OFF so existing special pages are unaffected until the owner turns them on.
  reservations: ['menu', 'tables'],
};

export function pluginDefaultsOn(template, id, kind) {
  // "Special" (Blank basic) pages are a clean universal canvas: EVERY module
  // starts OFF and the owner opts in to the ones they want.
  if (kind === 'special') return false;
  return !((DEFAULT_OFF[template] || []).includes(id));
}

// Single source of truth for whether a plugin is active on a page.
// `plugins[id]` undefined → template/kind default; explicit true/false overrides.
export function pluginEnabled(template, plugins, id, kind) {
  const v = (plugins || {})[id];
  if (v === undefined || v === null) return pluginDefaultsOn(template, id, kind);
  return !!v;
}

// Content sections the owner can drag to reorder on the public page.
// The booking / calendar block stays fixed at the top and is NOT in this list.
export const REORDERABLE = {
  reservations: ['about', 'menu', 'services', 'specialists', 'gift_cards', 'promotions', 'gallery', 'hours_map', 'faq', 'reviews', 'contacts', 'contact', 'contact_note'],
  menu: ['daily_special', 'menu', 'gift_cards', 'promotions', 'gallery', 'hours_map', 'faq', 'reviews', 'contact', 'contact_note'],
  business_card: ['about', 'gift_cards', 'hours_map', 'promotions', 'gallery', 'faq', 'reviews', 'contact', 'contact_note'],
};

export const SECTION_LABELS = {
  services: 'Pakalpojumi & cenas', specialists: 'Speciālisti', gift_cards: 'Dāvanu kartes',
  gallery: 'Galerija', faq: 'BUJ', reviews: 'Atsauksmes', contact: 'Kontaktforma',
  daily_special: 'Dienas piedāvājums', menu: 'Ēdienkarte', about: 'Par mums',
  hours_map: 'Darba laiks & atrašanās vieta', promotions: 'Akcijas & piedāvājumi',
  contact_note: 'Kontakta piezīme', contacts: 'Kontakti',
};

// Map a reorderable section id -> the plugin that must be available for it to
// appear. Sections not listed here are structural (always relevant for the type).
const SECTION_PLUGIN = {
  about: 'about', menu: 'menu', tables: 'tables', specialists: 'specialists',
  gift_cards: 'gift_cards', promotions: 'promotions', gallery: 'gallery',
  faq: 'faq', reviews: 'reviews', contacts: 'contacts', contact: 'contact_form',
};

// Whether a reorderable section is applicable to this template+kind. Keeps the
// editor's section list in sync with what the public page can actually render
// (e.g. a salon/clinic page must NOT offer the restaurant "Ēdienkarte" section).
function sectionAvailable(template, kind, id) {
  const avail = pluginsFor(template, kind);
  if (id === 'hours_map') return avail.includes('hours') || avail.includes('map');
  const plug = SECTION_PLUGIN[id];
  return plug ? avail.includes(plug) : true; // structural sections always kept
}

// Merge a saved order with the template defaults (keeps valid saved ids first,
// appends any new/missing section ids so nothing ever disappears). When `kind`
// is given, sections whose module is not available for that kind are dropped.
export function orderedSections(template, saved = [], kind) {
  let base = REORDERABLE[template] || [];
  if (kind) base = base.filter((id) => sectionAvailable(template, kind, id));
  const valid = (saved || []).filter((id) => base.includes(id));
  return [...valid, ...base.filter((id) => !valid.includes(id))];
}

// ---- Images --------------------------------------------------------------
const SALON = 'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const SALON2 = 'https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const SALON3 = 'https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const CLINIC = 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const CLINIC2 = 'https://images.unsplash.com/photo-1728342057953-94bfad8f0e7e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const CLINIC3 = 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const GYM = 'https://images.unsplash.com/photo-1637430308606-86576d8fef3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const GYM2 = 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const GYM3 = 'https://images.unsplash.com/photo-1576678927484-cc907957088c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const AUTO = 'https://images.unsplash.com/photo-1618312980096-873bd19759a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const AUTO2 = 'https://images.unsplash.com/photo-1610092708835-af669294f3f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const AUTO3 = 'https://images.unsplash.com/photo-1605822167835-d32696aef686?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const EDU = 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const EDU2 = 'https://images.unsplash.com/photo-1570616969692-54d6ba3d0397?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const EDU3 = 'https://images.unsplash.com/photo-1592303637753-ce1e6b8a0ffb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const STAY = 'https://images.unsplash.com/photo-1631447328378-2cf2499fb0ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const STAY2 = 'https://images.unsplash.com/photo-1684758117538-cd2100ca3067?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const STAY3 = 'https://images.unsplash.com/photo-1785530309281-5afde6509b0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const RE = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const RE2 = 'https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const RE3 = 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const CRAFT1 = 'https://images.unsplash.com/photo-1660796334938-cf0b03be7e6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const CRAFT2 = 'https://images.unsplash.com/photo-1579196179453-1531eddf62e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const CRAFT3 = 'https://images.unsplash.com/photo-1626252685663-64c6bf60afb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const RESTA = 'https://images.unsplash.com/photo-1583354608715-177553a4035e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const FOOD1 = 'https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const FOOD2 = 'https://images.unsplash.com/photo-1514326640560-7d063ef2aed5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const VR1 = 'https://images.unsplash.com/photo-1616588589676-62b3bd4ff6d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const VR2 = 'https://images.pexels.com/photos/7562281/pexels-photo-7562281.jpeg?auto=compress&cs=tinysrgb&w=1000';
const VR3 = 'https://images.unsplash.com/photo-1653158861306-e5b3804f6115?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
// Portraits
const PW1 = 'https://images.unsplash.com/photo-1607503873903-c5e95f80d7b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';
const PM1 = 'https://images.unsplash.com/photo-1590086782792-42dd2350140d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';
const PM2 = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';
const PM3 = 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';

const SLOTS = ['10:00', '12:00', '14:00', '16:00', '18:00'];
const WEEK = [0, 1, 2, 3, 4];

const sampleReviews = (names) => names.map((n, i) => ({ author: n, rating: 5 - (i % 2), text: n.split(' ')[0] + ' ieteiktu šo vietu ikvienam — profesionāla attieksme un lielisks rezultāts.' }));
const sampleFaq = (pairs) => pairs.map(([q, a]) => ({ q, a }));

// ---- Demos keyed by template-chooser card id -----------------------------
export const DEMOS = {
  reservations: {
    template: 'reservations',
    owner: { name: 'VR Piedzīvojumu Nams', region: 'Rīga' },
    reservations: {
      kind: 'general',
      tagline: 'Virtuālās realitātes istabas visai ģimenei un draugu kompānijām',
      cover_url: VR1, time_slots: ['12:00', '14:00', '16:00', '18:00', '20:00'],
      working_hours: 'P.–Ce. 14:00–22:00\nPk.–Sv. 11:00–23:00',
      address: 'Brīvības iela 103, Rīga', lat: 56.9589, lng: 24.1265,
      promotions: [{ title: 'Darbdienu cena -20%', description: 'Līdz plkst. 16:00' }, { title: 'Dzimšanas dienas pakete', description: 'Telpa + torte dāvanā' }],
      services: [
        { id: 'd1', name: 'VR istaba (1 stunda)', price: 25, unit: 'hour', description: 'Līdz 4 spēlētājiem, 40+ spēles' },
        { id: 'd2', name: 'Grupas piedzīvojums (2h)', price: 89, unit: 'group', description: 'Komandas saliedēšanai (līdz 8 cilv.)' },
        { id: 'd3', name: 'Dzimšanas dienas pakete', price: 149, unit: 'package', description: 'VR + telpa + kliņģeris' },
      ],
      gift_cards: [{ name: 'Dāvanu karte', amount: 30 }, { name: 'Dāvanu karte', amount: 60 }],
      gallery: [VR1, VR2, VR3],
      contact_note: 'Rekomendējam rezervēt laiku vismaz dienu iepriekš. Grupām — zvani!',
      reviews: sampleReviews(['Edgars L.', 'Līga B.']),
      faq: sampleFaq([['Vai der bērniem?', 'Jā, no 8 gadu vecuma pieaugušo pavadībā.'], ['Vai var atcelt rezervāciju?', 'Jā, līdz 24h pirms laika bez maksas.']]),
    },
  },
  salon: {
    template: 'reservations',
    owner: { name: 'Studija Glow', region: 'Rīga' },
    reservations: {
      kind: 'salon',
      working_hours: 'P.–Pk. 10:00–19:00\nSestdien 10:00–16:00\nSvētdien slēgts',
      address: 'Tērbatas iela 25, Rīga', lat: 56.9565, lng: 24.1265,
      promotions: [{ title: '-20% pirmajai vizītei', description: 'Jauniem klientiem' }, { title: 'Dāvanu karte draugam', description: 'Uzdāvini skaistumu' }],
      tagline: 'Matu, nagu un sejas kopšana vienuviet — pieraksties vizītei tiešsaistē',
      cover_url: SALON, time_slots: ['10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'],
      services: [
        { id: 's1', name: 'Sieviešu matu griezums & veidošana', price: 28, unit: 'package', duration_min: 60, image_url: SALON2, description: 'Konsultācija, mazgāšana, griezums, fēns' },
        { id: 's2', name: 'Matu krāsošana', price: 55, unit: 'package', duration_min: 120, description: 'Vienā tonī, ieskaitot kopšanu' },
        { id: 's3', name: 'Manikīrs ar gēllaku', price: 30, unit: 'package', duration_min: 75, description: 'Noturīgs pārklājums līdz 3 nedēļām' },
      ],
      specialists: [
        { id: 'sp1', name: 'Anna Bērziņa', role: 'Matu stiliste', photo_url: PW1, work_days: WEEK, off_dates: [] },
        { id: 'sp2', name: 'Kārlis Ozols', role: 'Nagu meistars', photo_url: PM1, work_days: [1, 2, 3, 4, 5], off_dates: [] },
      ],
      gift_cards: [{ name: 'Dāvanu karte', amount: 30 }, { name: 'Dāvanu karte', amount: 50 }, { name: 'Dāvanu karte', amount: 100 }],
      gallery: [SALON, SALON2, SALON3],
      contact_note: 'Pieraksts obligāts. Kavējoties vairāk par 15 min, laiks var tikt nodots citam klientam.',
      reviews: sampleReviews(['Ieva K.', 'Santa P.']),
      faq: sampleFaq([['Vai pieņemat bez pieraksta?', 'Pārsvarā strādājam ar iepriekšēju pierakstu.'], ['Ar ko norēķināties?', 'Skaidrā naudā un ar karti.']]),
    },
  },
  health: {
    template: 'reservations',
    owner: { name: 'Zobārstniecība Dentalux', region: 'Rīga' },
    reservations: {
      kind: 'health',
      tagline: 'Mūsdienīga zobārstniecība — piesakies vizītei pie speciālista',
      cover_url: CLINIC, time_slots: ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00'],
      working_hours: 'P.–Pk. 08:00–19:00\nSestdien 09:00–14:00\nSvētdien slēgts',
      address: 'Elizabetes iela 45, Rīga', lat: 56.9542, lng: 24.1189,
      services: [
        { id: 'h1', name: 'Konsultācija & apskate', price: 20, unit: 'package', duration_min: 30 },
        { id: 'h2', name: 'Higiēniskā tīrīšana', price: 45, unit: 'package', duration_min: 45 },
        { id: 'h3', name: 'Zoba plombēšana', price: 60, unit: 'package', duration_min: 60 },
      ],
      specialists: [
        { id: 'hp1', name: 'Dr. Elīna Kalniņa', role: 'Zobārste', photo_url: PW1, work_days: [0, 1, 2, 3], off_dates: [] },
        { id: 'hp2', name: 'Dr. Jānis Liepa', role: 'Zobārsts-ķirurgs', photo_url: PM2, work_days: [1, 2, 3, 4], off_dates: [] },
      ],
      gallery: [CLINIC, CLINIC2, CLINIC3],
      contact_note: 'Lūdzam ierasties 10 min pirms vizītes. Līdzi ņem personu apliecinošu dokumentu.',
      reviews: sampleReviews(['Mārtiņš Z.', 'Dace L.']),
      faq: sampleFaq([['Vai pieņemat bērnus?', 'Jā, strādājam ar visu vecumu pacientiem.'], ['Vai ir maksājums pa daļām?', 'Lielākām procedūrām — jā, pārrunājams uz vietas.']]),
    },
  },
  fitness: {
    template: 'reservations',
    owner: { name: 'Fitnesa Studija Pulse', region: 'Jūrmala' },
    reservations: {
      kind: 'fitness',
      tagline: 'Treniņi, grupu nodarbības un personīgie treneri — piesakies nodarbībai',
      cover_url: GYM, time_slots: ['07:00', '09:00', '12:00', '17:00', '18:00', '19:00'],
      working_hours: 'Katru dienu 06:00–23:00',
      address: 'Jomas iela 54, Jūrmala', lat: 56.9680, lng: 23.7794,
      promotions: [{ title: 'Pirmā nodarbība bez maksas', description: 'Jauniem klientiem' }, { title: '-30% gada abonementam', description: 'Rudens piedāvājums' }],
      services: [
        { id: 'f1', name: 'Personīgā treniņa stunda', price: 25, unit: 'hour', duration_min: 60 },
        { id: 'f2', name: 'Grupu nodarbība', price: 8, unit: 'group', duration_min: 60 },
        { id: 'f3', name: 'Mēneša abonements', price: 45, unit: 'package', description: 'Neierobežota pieeja zālei' },
      ],
      specialists: [
        { id: 'fp1', name: 'Roberts Vītols', role: 'Personīgais treneris', photo_url: PM3, work_days: WEEK, off_dates: [] },
        { id: 'fp2', name: 'Anna Bērziņa', role: 'Joga & stiepšanās', photo_url: PW1, work_days: [1, 3, 5], off_dates: [] },
      ],
      gallery: [GYM, GYM2, GYM3],
      contact_note: 'Pirmā izmēģinājuma nodarbība bez maksas — piesakies!',
      reviews: sampleReviews(['Kristaps B.', 'Elīna R.']),
      faq: sampleFaq([['Vai vajag iepriekšēju pieredzi?', 'Nē, nodarbības piemērotas visiem līmeņiem.'], ['Vai ir dušas?', 'Jā, pieejamas ģērbtuves ar dušām.']]),
    },
  },
  auto: {
    template: 'reservations',
    owner: { name: 'Auto Serviss GearUp', region: 'Rīga' },
    reservations: {
      kind: 'auto',
      tagline: 'Remonts, riepu maiņa un diagnostika — piesaki savu auto servisam',
      cover_url: AUTO, time_slots: ['08:00', '10:00', '12:00', '14:00', '16:00'],
      working_hours: 'P.–Pk. 08:00–18:00\nSestdien 09:00–15:00',
      address: 'Krasta iela 68, Rīga', lat: 56.9330, lng: 24.1398,
      promotions: [{ title: 'Bezmaksas diagnostika', description: 'Pie eļļas maiņas' }, { title: 'Sezonas riepu glabāšana', description: '-50% pirmajai sezonai' }],
      services: [
        { id: 'a1', name: 'Riepu maiņa (4 riepas)', price: 25, unit: 'package', duration_min: 40 },
        { id: 'a2', name: 'Eļļas un filtru maiņa', price: 45, unit: 'package', duration_min: 45 },
        { id: 'a3', name: 'Datordiagnostika', price: 30, unit: 'package', duration_min: 30 },
      ],
      gallery: [AUTO, AUTO2, AUTO3],
      contact_note: 'Rezerves daļas pasūtām dienas laikā. Sarežģītākiem remontiem sazvanāmies iepriekš.',
      reviews: sampleReviews(['Aigars K.', 'Normunds P.']),
      faq: sampleFaq([['Vai dodat garantiju?', 'Jā, darbiem 12 mēnešu garantija.'], ['Vai var atstāt auto uz dienu?', 'Jā, iespējams atstāt no rīta.']]),
    },
  },
  education: {
    template: 'reservations',
    owner: { name: 'Mācību Centrs Prātnieks', region: 'Rīga' },
    reservations: {
      kind: 'education',
      tagline: 'Kursi, meistarklases un privātstundas — piesakies un mācies',
      cover_url: EDU, time_slots: ['10:00', '12:00', '15:00', '17:00', '18:00'],
      promotions: [{ title: '-15% grupu kursiem', description: 'Piesakoties līdz mēneša beigām' }, { title: 'Pirmā nodarbība bez maksas', description: 'Izmēģini pirms pieteikšanās' }],
      services: [
        { id: 'e1', name: 'Angļu valodas privātstunda', price: 18, unit: 'hour', duration_min: 60 },
        { id: 'e2', name: 'Programmēšanas kurss (grupā)', price: 120, unit: 'package', description: '8 nodarbības' },
        { id: 'e3', name: 'Meistarklase', price: 25, unit: 'group', duration_min: 120 },
      ],
      specialists: [
        { id: 'ep1', name: 'Līga Zariņa', role: 'Valodu pasniedzēja', photo_url: PW1, work_days: WEEK, off_dates: [] },
        { id: 'ep2', name: 'Toms Krūmiņš', role: 'IT pasniedzējs', photo_url: PM2, work_days: [1, 2, 3, 4], off_dates: [] },
      ],
      gallery: [EDU, EDU2, EDU3],
      contact_note: 'Grupas veidojam 4–8 cilvēku sastāvā. Jautā par pieejamiem laikiem!',
      reviews: sampleReviews(['Agnese V.', 'Didzis M.']),
      faq: sampleFaq([['Vai izsniedzat sertifikātu?', 'Jā, pēc kursa pabeigšanas.'], ['Vai var pievienoties vidū?', 'Atsevišķiem kursiem — jā, pārrunājams.']]),
    },
  },
  stay: {
    template: 'reservations',
    owner: { name: 'Lauku Māja Kļavas', region: 'Cēsis' },
    reservations: {
      kind: 'stay', request_only: true,
      tagline: 'Mierīga brīvdienu māja ar pirti dabas ielokā — rezervē savu uzturēšanos',
      cover_url: STAY,
      working_hours: 'Reģistrēšanās no 15:00\nIzrakstīšanās līdz 12:00',
      address: '«Kļavas», Drabešu pagasts, Cēsu novads', lat: 57.2680, lng: 25.1250,
      promotions: [{ title: 'Darbdienu nakts -25%', description: 'Svētdien–ceturtdien' }, { title: 'Pirts dāvanā', description: 'Rezervējot visu māju 2 naktīm' }],
      services: [
        { id: 'st1', name: 'Nakts standarta numurā', price: 55, unit: 'night', description: '2 personām, brokastis iekļautas' },
        { id: 'st2', name: 'Visa māja (diennakts)', price: 220, unit: 'night', description: 'Līdz 8 personām' },
        { id: 'st3', name: 'Pirts (2h)', price: 40, unit: 'package', description: 'Malkas pirts ar dīķi' },
      ],
      gallery: [STAY, STAY2, STAY3],
      contact_note: 'Minimālā uzturēšanās nedēļas nogalēs — 2 naktis. Dzīvnieki pēc vienošanās.',
      reviews: sampleReviews(['Ģirts un Anda', 'Zane K.']),
      faq: sampleFaq([['Cikos ir ierašanās?', 'No plkst. 15:00, izrakstīšanās līdz 12:00.'], ['Vai ir WiFi?', 'Jā, bezmaksas internets visā mājā.']]),
    },
  },
  trade: {
    template: 'reservations',
    owner: { name: 'Amatnieks Ozols', region: 'Jelgava' },
    reservations: {
      kind: 'trade', request_only: true,
      tagline: 'Santehnika, elektrība un sīkie remontdarbi — pieprasi piedāvājumu vai atzvanu',
      cover_url: CRAFT1,
      services: [
        { id: 't1', name: 'Izsaukums & diagnostika', price: 20, unit: 'package', description: 'Rīgā un apkārtnē' },
        { id: 't2', name: 'Santehnikas darbi', price: 0, unit: 'package', description: 'Cena pēc apjoma' },
        { id: 't3', name: 'Elektromontāža', price: 0, unit: 'package', description: 'Cena pēc apjoma' },
      ],
      gallery: [CRAFT1, CRAFT2, CRAFT3],
      contact_note: 'Atbildam 1 darba dienas laikā. Steidzamiem darbiem — zvani.',
      reviews: sampleReviews(['Viktors L.', 'Ināra B.']),
      faq: sampleFaq([['Vai strādājat brīvdienās?', 'Steidzamos gadījumos — jā.'], ['Vai izrakstāt rēķinu?', 'Jā, strādājam arī ar uzņēmumiem.']]),
    },
  },
  realestate: {
    template: 'reservations',
    owner: { name: 'Nekustamie Īpašumi Nams', region: 'Rīga' },
    reservations: {
      kind: 'realestate', request_only: true,
      tagline: 'Dzīvokļu un māju pārdošana un noma — piesaki objekta apskati',
      cover_url: RE,
      address: 'Ģertrūdes iela 34, Rīga', lat: 56.9550, lng: 24.1280,
      services: [
        { id: 'r1', name: '3 ist. dzīvoklis, Centrs', price: 165000, unit: 'none', description: '72 m², renovēts' },
        { id: 'r2', name: 'Privātmāja, Mārupe', price: 310000, unit: 'none', description: '140 m², zeme 800 m²' },
        { id: 'r3', name: '2 ist. dzīvoklis (noma)', price: 550, unit: 'month', description: 'Mēbelēts' },
      ],
      gallery: [RE, RE2, RE3],
      contact_note: 'Apskates piesakām 1–2 dienas iepriekš. Konsultācija bez maksas.',
      reviews: sampleReviews(['Jolanta S.', 'Edijs R.']),
      faq: sampleFaq([['Vai palīdzat ar hipotēku?', 'Jā, sadarbojamies ar bankām.'], ['Kāda ir komisija?', 'Atkarīga no darījuma — pārrunājama.']]),
    },
  },
  menu: {
    template: 'menu',
    owner: { name: 'Kafejnīca Osta', region: 'Liepāja' },
    menu: {
      tagline: 'Mājas virtuve ar Kurzemes akcentu, Liepājas ostas malā',
      cover_url: RESTA,
      working_hours: 'Katru dienu 11:00–22:00',
      address: 'Kuršu laukums 1, Liepāja', lat: 56.5086, lng: 21.0132,
      promotions: [{ title: 'Biznesa pusdienas -15%', description: 'Darbdienās 12:00–15:00' }],
      menu_ordering_enabled: true,
      daily_special_allow_pickup: true, daily_special_allow_delivery: true,
      daily_special_delivery_fee: 2.5, daily_special_min_delivery: 10, daily_special_free_delivery_from: 25, daily_special_delivery_radius_km: 5,
      allow_online: true, allow_onsite: true,
      daily_special: 'Dienas komplekts: zupa + pamatēdiens + dzēriens — 8.90 €',
      reservations_enabled: true, time_slots: SLOTS,
      categories: [
        { name: 'Uzkodas', items: [
          { name: 'Siltmaizes ar sieru un ķiplokiem', price: 4.5, description: 'Ceptas krāsnī', tags: ['veģetārs'], image_url: FOOD1, option_groups: [{ name: 'Porcija', choices: [{ label: 'Maza', price_delta: -1 }, { label: 'Vidēja', price_delta: 0 }, { label: 'Liela', price_delta: 2 }] }, { name: 'Asums', choices: [{ label: 'Neass', price_delta: 0 }, { label: 'Ass', price_delta: 0 }] }], addons: [{ label: 'Papildu siers', price: 1 }, { label: 'Ķiploku mērce', price: 0.5 }] },
          { name: 'Kūpināta laša tartars', price: 7.9, description: 'Ar avokado un citronu', tags: ['jauns'] },
        ] },
        { name: 'Pamatēdieni', items: [
          { name: 'Cepta menca ar sezonas dārzeņiem', price: 13.5, description: 'Vietējā zveja' },
          { name: 'Karbonāde ar kartupeļiem', price: 9.9, description: 'Klasika, kā vecmāmiņa gatavoja' },
          { name: 'Ķirbju risoto', price: 10.5, description: 'Ar parmezānu', tags: ['veģetārs'] },
        ] },
        { name: 'Deserti', items: [
          { name: 'Siera kūka ar mežlogām', price: 5.5, description: '' },
          { name: 'Karstā šokolāde', price: 3.9, description: '' },
        ] },
      ],
      gallery: [FOOD1, FOOD2, RESTA],
      reviews: sampleReviews(['Jānis K.', 'Agnese D.']),
      faq: sampleFaq([['Vai der grupām?', 'Jā, grupām virs 6 personām lūdzam pieteikties iepriekš.'], ['Vai ir veģetārie ēdieni?', 'Jā, ēdienkartē ir vairāki veģetārie piedāvājumi.']]),
      contact_note: 'Grupām virs 6 personām lūdzam pieteikties iepriekš.',
    },
  },
  business_card: {
    template: 'business_card',
    owner: { name: 'Koka Darbnīca «Ozols»', region: 'Cēsis' },
    business_card: {
      tagline: 'Rokām darināti koka izstrādājumi un mēbeles pēc pasūtījuma',
      cover_url: CRAFT1,
      about: 'Esam ģimenes darbnīca ar vairāk nekā 15 gadu pieredzi. Darinām mēbeles, dāvanas un interjera priekšmetus no vietējā ozola un bērza. Katrs darbs ir unikāls.',
      address: 'Rīgas iela 12, Cēsis', lat: 57.3126, lng: 25.2749,
      working_hours: 'P.–Pk. 09:00–18:00\nSestdien 10:00–15:00\nSvētdien slēgts',
      contacts: { phone: '+371 26000000', email: 'info@ozols-darbnica.lv', website: 'www.ozols-darbnica.lv', facebook: 'facebook.com/ozolsdarbnica', instagram: 'instagram.com/ozols.darbnica' },
      promotions: [
        { title: '-15% pirmajam pasūtījumam', description: 'Jauniem klientiem visa gada garumā' },
        { title: 'Bezmaksas konsultācija', description: 'Izbraucam un izmērām jūsu telpu' },
      ],
      gallery: [CRAFT1, CRAFT2, CRAFT3],
      faq: sampleFaq([['Cik ilgā laikā izgatavojat pasūtījumu?', 'Parasti 2–4 nedēļu laikā atkarībā no darba apjoma.'], ['Vai piegādājat?', 'Jā, piegādājam visā Latvijā par vienošanos.']]),
      reviews: sampleReviews(['Mārtiņš O.', 'Baiba L.']),
    },
  },
};

export function demoKind(demoId) {
  const d = DEMOS[demoId];
  if (!d) return { template: 'reservations', kind: 'general' };
  return { template: d.template, kind: d[d.template]?.kind || 'general' };
}
